import React, { useState, useRef, useEffect } from 'react';
import { Fab, Badge, Box } from '@mui/material';
import { ShoppingCart } from '@mui/icons-material';
import { useCart } from '../contexts/CartContext';

const DraggableCart = ({ onCartClick }) => {
  const { totalItems } = useCart();
  const [position, setPosition] = useState(() => {
    // Set initial position to bottom right but higher up to avoid overlapping with GEMMA chatbot
    if (typeof window !== 'undefined') {
      return {
        x: Math.max(0, window.innerWidth - 72),
        y: Math.max(0, window.innerHeight - 180) // Positioned even higher to avoid GEMMA chatbot
      };
    }
    return { x: 0, y: 0 };
  });
  const [isDragging, setIsDragging] = useState(false);
  const [hasMoved, setHasMoved] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [touchStartPos, setTouchStartPos] = useState(null);
  const cartRef = useRef(null);

  // Load saved position from localStorage or set default bottom right position
  useEffect(() => {
    const calculateBottomRightPosition = () => {
      return {
        x: Math.max(0, window.innerWidth - 72),
        y: Math.max(0, window.innerHeight - 180) // Positioned even higher to avoid GEMMA chatbot
      };
    };

    // Small delay to ensure window dimensions are available
    const timer = setTimeout(() => {
      const savedPosition = localStorage.getItem('cartPosition');
      if (savedPosition) {
        try {
          const parsedPosition = JSON.parse(savedPosition);
          // Validate that saved position is still within viewport
          const bottomRight = calculateBottomRightPosition();
          const newPosition = {
            x: Math.min(parsedPosition.x, bottomRight.x),
            y: Math.min(parsedPosition.y, bottomRight.y)
          };
          setPosition(newPosition);
          
          // Check if the loaded position is close to original position
          const distance = Math.sqrt(
            Math.pow(newPosition.x - bottomRight.x, 2) + 
            Math.pow(newPosition.y - bottomRight.y, 2)
          );
          setHasMoved(distance > 10);
        } catch (error) {
          console.error('Error loading cart position:', error);
          setPosition(calculateBottomRightPosition());
          setHasMoved(false);
        }
      } else {
        // Set default bottom right position if no saved position
        setPosition(calculateBottomRightPosition());
        setHasMoved(false);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  // Save position to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('cartPosition', JSON.stringify(position));
  }, [position]);

  // Handle window resize to keep cart in viewport
  useEffect(() => {
    const handleResize = () => {
      const maxX = window.innerWidth - 72;
      const maxY = window.innerHeight - 72;
      
      setPosition(prevPosition => ({
        x: Math.min(prevPosition.x, maxX),
        y: Math.min(prevPosition.y, maxY)
      }));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleMouseDown = (e) => {
    if (e.button === 0) { // Left click only
      setIsDragging(true);
      const rect = cartRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
      e.preventDefault();
    }
  };

  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    const rect = cartRef.current.getBoundingClientRect();
    
    // Store initial touch position for tap detection
    setTouchStartPos({
      x: touch.clientX,
      y: touch.clientY
    });
    
    setDragOffset({
      x: touch.clientX - rect.left,
      y: touch.clientY - rect.top
    });
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;
      
      // Constrain to viewport bounds
      const maxX = window.innerWidth - 56; // 56px is the cart width
      const maxY = window.innerHeight - 56; // 56px is the cart height
      
      const newPosition = {
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY))
      };
      
      setPosition(newPosition);
      
      // Check if cart has moved from original position
      const originalPosition = {
        x: Math.max(0, window.innerWidth - 72),
        y: Math.max(0, window.innerHeight - 180) // Positioned even higher to avoid GEMMA chatbot
      };
      
      const distance = Math.sqrt(
        Math.pow(newPosition.x - originalPosition.x, 2) + 
        Math.pow(newPosition.y - originalPosition.y, 2)
      );
      
      if (distance > 10) {
        setHasMoved(true);
      }
    }
  };

  const handleTouchMove = (e) => {
    if (!touchStartPos) return;
    
    const touch = e.touches[0];
    
    // Check if touch has moved enough to be considered a drag
    const moveDistance = Math.sqrt(
      Math.pow(touch.clientX - touchStartPos.x, 2) + 
      Math.pow(touch.clientY - touchStartPos.y, 2)
    );
    
    // Only start dragging if moved more than 15px (increased threshold for mobile)
    if (moveDistance > 15 && !isDragging) {
      setIsDragging(true);
      e.preventDefault();
    }
    
    if (isDragging) {
      e.preventDefault();
      const newX = touch.clientX - dragOffset.x;
      const newY = touch.clientY - dragOffset.y;
      
      // Constrain to viewport bounds
      const maxX = window.innerWidth - 56;
      const maxY = window.innerHeight - 56;
      
      const newPosition = {
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY))
      };
      
      setPosition(newPosition);
      
      // Check if cart has moved from original position
      const originalPosition = {
        x: Math.max(0, window.innerWidth - 72),
        y: Math.max(0, window.innerHeight - 180)
      };
      
      const distance = Math.sqrt(
        Math.pow(newPosition.x - originalPosition.x, 2) + 
        Math.pow(newPosition.y - originalPosition.y, 2)
      );
      
      if (distance > 10) {
        setHasMoved(true);
      }
    }
  };

  const handleMouseUp = () => {
    if (isDragging) {
      setIsDragging(false);
    }
  };

  const handleTouchEnd = (e) => {
    if (!touchStartPos) return;
    
    // Calculate if this was a tap or drag
    const touch = e.changedTouches[0];
    const moveDistance = touch ? Math.sqrt(
      Math.pow(touch.clientX - touchStartPos.x, 2) + 
      Math.pow(touch.clientY - touchStartPos.y, 2)
    ) : 0;
    
    if (isDragging) {
      setIsDragging(false);
    } else if (moveDistance < 15) {
      // This was a tap, not a drag - trigger the cart click
      e.preventDefault();
      setTimeout(() => {
        onCartClick();
      }, 10);
    }
    
    setTouchStartPos(null);
  };

  const handleClick = (e) => {
    // Only trigger cart click if we're not dragging and haven't moved significantly
    if (!isDragging && !hasMoved) {
      onCartClick();
    }
  };

  // Add global mouse and touch event listeners
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isDragging, dragOffset]);

  // Add touch event listeners for proper tap detection when touch starts
  useEffect(() => {
    if (touchStartPos) {
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);
      
      return () => {
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [touchStartPos]);

  return (
    <Box
      ref={cartRef}
      sx={{
        position: 'fixed',
        left: position.x,
        top: position.y,
        zIndex: 1001,
        cursor: isDragging ? 'grabbing' : 'grab',
        userSelect: 'none',
        touchAction: isDragging ? 'none' : 'auto'
      }}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      onClick={handleClick}
    >
      <Fab
        color="primary"
        aria-label="cart"
        sx={{
          width: 56,
          height: 56,
          boxShadow: isDragging ? '0 8px 25px rgba(0,0,0,0.3)' : '0 4px 12px rgba(0,0,0,0.15)',
          transition: isDragging ? 'none' : 'all 0.3s ease',
          '&:hover': {
            transform: isDragging ? 'none' : 'scale(1.05)',
            boxShadow: isDragging ? '0 8px 25px rgba(0,0,0,0.3)' : '0 6px 20px rgba(0,0,0,0.2)'
          }
        }}
      >
        <Badge badgeContent={totalItems} color="error">
          <ShoppingCart />
        </Badge>
      </Fab>
    </Box>
  );
};

export default DraggableCart; 